import React from "react";
import _ from "lodash";
export interface IDataTableGroupDictionary<T> extends _.Dictionary<T> { }

export type ColumnSingleValue = string | number | Date;
export type ColumnMultipleValue = string[] | number[] | Date[];
export type ColumnValue = ColumnSingleValue | ColumnMultipleValue;

export interface DataTableExportConfiguration {
  mode?: "csv" | "xml" | "none";
  columnDelimiter?: string;
  includeHeaders?: boolean;
  decimalSeparator?: "." | ",";
  quoteText?: boolean;
  exportOnlyVisibleColumns?: boolean;
}

let ExportConfigurationDefault: DataTableExportConfiguration = {
  mode: "xml",
  columnDelimiter: ", ",
  includeHeaders: true,
  decimalSeparator: ",",
  quoteText: true,
  exportOnlyVisibleColumns: true
}

export { ExportConfigurationDefault as ExportConfigurationDefault }

export interface IColumn<TProp, TState = {}>
  extends React.Component<TProp, TState> {
  getSummaryItem(elements: any[]): React.ReactElement<any> | string | number;
  getHeader(elements: any[]): React.ReactElement<any> | string | number;
  getCell(row: any): React.ReactNode | null;
  getCommands(): React.ReactNode[];
  getDefaultGroupingIndex(): number | undefined;
  getTitle(): string | undefined;
  style?: React.CSSProperties;
  canGroup(): boolean;
  canOrder(): boolean;
  canFilter(): boolean;
  canHide(): boolean;
  canExport(): boolean;
  getGroupTemplate: (rowData: any) => React.ReactElement<any> | string | number;
  getFilterTemplate: () => ((rowData: any) => ColumnValue) | null;
  getExportTemplate: () => ((rowData: any) => ColumnValue) | null;
  getFilterBy: () => ((rowData: any) => ColumnValue) | false | null | undefined;
  isHidden(): boolean;

}

export interface ColumnProps<T> {
  header?: React.ReactElement<any> | string | number;
  template: (rowData: T) => React.ReactNode | null;
  groupTemplate?: (rowData: any) => React.ReactElement<any> | string | number | null;
  groupBy?: (rowData: T) => string | number;
  orderBy?: (rowData: T) => string | number | Date;
  orderable?: boolean;
  groupable?: boolean;
  className?: string;
  commands?: React.ReactNode[];
  classNameSelector?: (item: T) => string;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  headerClassName?: string;
  footerClassName?: string;
  defaultGroupingIndex?: number;
  hidden?: boolean;
  valueForFooter?: (rowsData: T[]) => React.ReactElement<any> | string | number;
  filterBy?: ((rowData: T) => ColumnValue) | false | null;
  filterTemplate?: (rowData: T) => ColumnValue;
  title?: string | undefined;
  exportable?: boolean | ((rowData: T) => ColumnValue);
}

export class Column<T> extends React.Component<ColumnProps<T>>
  implements IColumn<ColumnProps<T>> {
  getSummaryItem(elements: T[]) {
    return "";
  }
  getHeadRow() {
    return <span />;
  }
  getHeader(elements: T[]) {
    return this.props.header || "";
  }
  getCommands() {
    return this.props.commands || [];
  }
  getCell(row: T): React.ReactNode | null {
    return this.props.template(row);
  }
  group(elements: T[]): IDataTableGroupDictionary<any> {
    return _.groupBy(elements, this.props.groupBy || this.props.template);
  }
  getGroupTemplate(groupKey: any) {
    return this.props.groupTemplate && this.props.groupTemplate(groupKey) || groupKey;
  }

  getFilterTemplate() {
    return this.props.filterTemplate || this.props.filterBy || null;
  }

  getFilterBy() {
    return this.props.filterBy;
  }

  getTitle() {
    return this.props.title;
  }

  orderBy<T>(f: (rowData: T) => Date | React.ReactNode) {
    return (r: T) => {
      const value = f(r);
      if (typeof value === "string") {
        return value.toUpperCase();
      }
      else if (value instanceof Date) {
        return value.valueOf();
      }
      else {
        return value;
      }
    };
  }

  order(elements: T[], ascending: boolean): T[] {
    const specified = this.orderBy(this.props.orderBy || this.props.template);
    return _.orderBy(elements, specified, [ascending ? "asc" : "desc"]);
  }

  filter(elements: T[], selectedValues: ColumnSingleValue[]): T[] {

    const { filterBy } = this.props;
    if (filterBy && selectedValues?.length) {
      return elements.filter(x => {
        const value = filterBy(x);
        if (!Array.isArray(value))
          return selectedValues.indexOf(value) < 0;

        return value.some((v: ColumnSingleValue) => selectedValues.indexOf(v) < 0)
      })
    }

    return elements;
  }

  canGroup() {
    return this.props.groupable === undefined || this.props.groupable;
  }

  canOrder() {
    return this.props.orderable === undefined || this.props.orderable;
  }

  canFilter() {
    return this.props.filterBy ? true : false;
  }

  getDefaultGroupingIndex() {
    return this.props.defaultGroupingIndex;
  }

  getValueForFooter() {
    return this.props.valueForFooter;
  }

  canHide() {
    return this.props.header != null && (typeof this.props.header === "string") && (this.props.header as string).length > 0;
  }

  isHidden() {
    return this.props.hidden || false;
  }

  canExport() {
    return this.props.exportable != null ? (this.props.exportable && this.isExportFunction(this.props.exportable)) : true;
  }

  getExportTemplate() {
    return this.isExportFunction(this.props.exportable) ? this.props.exportable as ((rowData: any) => ColumnValue) : null;
  }

  private isExportFunction(x: any): x is (rowData: any) => ColumnValue {
    return (typeof x === 'function') && (x.length === 1); // or whatever test
  }
}

export class State<T> {
  constructor(storagekey?: string) {
    if (storagekey) {
      const oTypeJson = this.getItemFromStorage("OrderType", storagekey);
      if (oTypeJson) this.orderType = JSON.parse(oTypeJson);

      const groupsJson = this.getItemFromStorage("GroupIndexes", storagekey);
      if (groupsJson) this.groupIndexes = JSON.parse(groupsJson);

      const stringFilter = this.getItemFromStorage("StringFilter", storagekey);
      if (stringFilter) this.filterString = stringFilter;

      const currentPageSize = this.getItemFromStorage("PageSize", storagekey);
      if (currentPageSize) {
        let converted = parseInt(currentPageSize);
        if (!isNaN(converted)) this.customPageSize = converted;
      }

      const currentPage = this.getItemFromStorage("CurrentPage", storagekey);
      if (currentPage) {
        let converted = parseInt(currentPage);
        if (!isNaN(converted)) this.currentPage = converted;
      }

      const hiddenColsJson = this.getItemFromStorage("OptionalColumns", storagekey);
      if (hiddenColsJson) this.hiddenColumns = JSON.parse(hiddenColsJson);

    }
  }
  currentPage: number = 0;
  customPageSize: number;
  selectedItems: T[] = [];
  itemsDisplayed: T[] = [];
  filteredItems: T[] = [];
  internalData: IDataTableGroupDictionary<T[]> | T[] = {};
  allItems: T[] = [];
  filterString: string = "";
  columns: Column<T>[] = [];
  hiddenColumns: { [columnName: string]: boolean } = {};
  orderType: { columnIndex?: number; ascending: boolean } = {
    columnIndex: undefined,
    ascending: true
  };
  groupIndexes: number[] = [];
  filteredColumns: { [key: number]: ColumnSingleValue[] } = {}
  collapsedGroups: { index: number; key: string; parent: string }[] = [];

  private getItemFromStorage = (key: string, storageKey: string) => {
    return localStorage.getItem(`DataTable${storageKey}${key}`);
  }
}
